class Toolbarmobile extends HTMLElement {
  constructor() {
    super();

    if (theme.config.mqlSmall || theme.config.isTouch) this.init();
  }

  init() {
    this.activate();
    this.setHeight();
    window.addEventListener('resize', theme.utils.rafThrottle(this.setHeight.bind(this)));
  }

  activate() {
    const header = document.querySelector(".header[data-sticky-state='inactive]");
    header === null
      ? this.classList.add("active")
      : this.classList.remove("active");
  }

  setHeight() {
    document.body.style.setProperty('--toolbar-mobile-height', `${this.clientHeight}px`);
  }
}
customElements.define("toolbar-mobile", Toolbarmobile);

class MobileCartPill extends HTMLElement {
  constructor() {
    super();
    this.refresh = this.refresh.bind(this);
    this.queueRefresh = this.queueRefresh.bind(this);
  }

  connectedCallback() {
    this.querySelector('[data-mobile-cart-open]')?.addEventListener('click', () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(this.querySelector('[data-mobile-cart-open]'));
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.refresh);
      this.unsubscribeQuantityUpdate = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.refresh);
    }
    document.addEventListener('cart:updated', this.refresh);

    this.cartCountObserver = new MutationObserver(this.queueRefresh);
    this.cartCountObserver.observe(document.body, { childList: true, subtree: true, characterData: true });

    this.onProductSubmit = (event) => {
      if (!event.target.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')) return;
      window.setTimeout(this.refresh, 150);
      window.setTimeout(this.refresh, 450);
      window.setTimeout(this.refresh, 900);
    };
    document.addEventListener('submit', this.onProductSubmit, true);
  }

  disconnectedCallback() {
    this.unsubscribeCartUpdate?.();
    this.unsubscribeQuantityUpdate?.();
    this.cartCountObserver?.disconnect();
    document.removeEventListener('cart:updated', this.refresh);
    document.removeEventListener('submit', this.onProductSubmit, true);
    window.clearTimeout(this.refreshTimer);
    window.clearTimeout(this.offerTimer);
  }

  queueRefresh(mutations) {
    const cartCountChanged = mutations.some((mutation) => {
      const element = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      return element?.matches?.('[data-cart-count]') || element?.closest?.('[data-cart-count]') ||
        Array.from(mutation.addedNodes).some((node) => node.nodeType === Node.ELEMENT_NODE && (node.matches?.('[data-cart-count]') || node.querySelector?.('[data-cart-count]')));
    });
    if (!cartCountChanged) return;
    window.clearTimeout(this.refreshTimer);
    this.refreshTimer = window.setTimeout(this.refresh, 50);
  }

  async refresh() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      this.render(await response.json());
    } catch (error) {
      console.error('Unable to refresh mobile cart button:', error);
    }
  }

  render(cart) {
    const previousCount = Number(this.querySelector('[data-mobile-cart-count]')?.textContent || 0);
    this.syncHeaderCount(cart.item_count);
    this.hidden = cart.item_count === 0;
    if (this.hidden) return;

    const count = this.querySelector('[data-mobile-cart-count]');
    const label = this.querySelector('[data-mobile-cart-item-label]');
    const images = this.querySelector('[data-mobile-cart-images]');
    const button = this.querySelector('[data-mobile-cart-open]');
    const offer = this.querySelector('[data-mobile-cart-offer]');
    const progress = this.querySelector('[data-mobile-cart-progress]');

    if (count) count.textContent = cart.item_count;
    if (label) label.textContent = cart.item_count === 1 ? 'item' : 'items';
    if (button) button.setAttribute('aria-label', `Open cart, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);

    const offerState = this.getOfferState(cart.item_count);
    if (offer) offer.textContent = offerState.message;
    if (progress) progress.style.width = `${offerState.progress}%`;

    const unlockedTier = [3, 5, 7].includes(cart.item_count) && previousCount < cart.item_count;
    if (unlockedTier) {
      const unlockedMessages = {
        3: '3 for ₹599 unlocked · Single products only (no combos)',
        5: '5 for ₹799 unlocked · Single products only (no combos)',
        7: '7 for ₹999 unlocked · Single products only (no combos)',
      };
      this.classList.add('is-offer-unlocked');
      if (offer) offer.textContent = unlockedMessages[cart.item_count];
      window.clearTimeout(this.offerTimer);
      this.offerTimer = window.setTimeout(() => {
        this.classList.remove('is-offer-unlocked');
        if (offer) offer.textContent = this.getOfferState(cart.item_count).message;
      }, 1800);
    } else {
      this.classList.remove('is-offer-unlocked');
    }

    if (images) {
      images.replaceChildren();
      cart.items.slice(0, 2).forEach((item) => {
        if (!item.image) return;
        const wrapper = document.createElement('span');
        const image = document.createElement('img');
        wrapper.className = 'mobile-cart-pill__image';
        image.src = item.image;
        image.alt = '';
        image.width = 40;
        image.height = 40;
        wrapper.appendChild(image);
        images.appendChild(wrapper);
      });
    }
  }

  syncHeaderCount(itemCount) {
    const countText = itemCount >= 100 ? (window.cartStrings?.item_99 || '99+') : String(itemCount);

    document.querySelectorAll('.cart-icon--drawer').forEach((cartIcon) => {
      let bubble = cartIcon.querySelector('.cart-count-bubble');

      if (itemCount === 0) {
        bubble?.remove();
      } else {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true" data-cart-count></span><span class="visually-hidden" data-cart-text></span>';
          cartIcon.appendChild(bubble);
        }

        const count = bubble.querySelector('[data-cart-count]');
        const accessibleText = bubble.querySelector('[data-cart-text]');
        if (count && count.textContent !== countText) count.textContent = countText;
        if (accessibleText) {
          accessibleText.textContent = itemCount === 1
            ? (window.cartStrings?.item || '[count] item').replace('[count]', itemCount)
            : (window.cartStrings?.items || '[count] items').replace('[count]', itemCount);
        }
      }

      cartIcon.setAttribute('aria-label', `Cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
    });
  }

  getOfferState(itemCount) {
    let target;
    let message;
    if (itemCount < 3) {
      target = 3;
      const remaining = 3 - itemCount;
      message = `Add ${remaining} single product${remaining === 1 ? '' : 's'} · 3 for ₹599 (no combos)`;
    } else if (itemCount < 5) {
      target = 5;
      const remaining = 5 - itemCount;
      message = `Add ${remaining} single product${remaining === 1 ? '' : 's'} · 5 for ₹799 (no combos)`;
    } else if (itemCount < 7) {
      target = 7;
      const remaining = 7 - itemCount;
      message = `Add ${remaining} single product${remaining === 1 ? '' : 's'} · 7 for ₹999 (no combos)`;
    } else {
      target = itemCount;
      message = '7 for ₹999 unlocked · Single products only (no combos)';
    }
    return { message, progress: Math.min(100, Math.round((itemCount / target) * 100)) };
  }
}

if (!customElements.get('mobile-cart-pill')) customElements.define('mobile-cart-pill', MobileCartPill);
